/**
 * clasificacion.ts — qué dimensiones tiene que traer una línea.
 *
 * Está aparte de `catalogo.tsx` y sin nada de React A PROPÓSITO: es una regla,
 * no una pantalla, y las dos pantallas que escriben —`/asiento` y `/banco`—
 * tienen que contestar lo mismo. Una regla dentro de un componente se prueba
 * montando el componente, y entonces no se prueba.
 */
import type { CondicionDeClasificacion, CuentaReal } from '../datos/libros.ts';

/**
 * Las de la cuenta MÁS las que exige lo que la línea YA escribió.
 *
 * ▶ La segunda mitad no la puede decir una cuenta. El modelo exige `modalidad`
 * en «el OPEX del área de soporte», que no es una cuenta —`6110` es el salario
 * de todo el mundo— sino un valor de otra dimensión. Exigirla en todo el grupo
 * 6 la pediría en el salario de ventas y de I+D, donde no significa nada, y un
 * campo obligatorio sin significado se rellena con cualquier cosa.
 *
 * Ningún archivo de pantalla sabe que soporte lleva modalidad: la regla viene
 * de `control.clasificacion_condicional` con el catálogo.
 */
export function dimensionesQueExige(
  cuenta: CuentaReal | null | undefined,
  condicional: readonly CondicionDeClasificacion[],
  puestas: Readonly<Record<string, string>>,
): readonly string[] {
  const dims = new Set(cuenta?.clasificacion ?? []);
  for (const c of condicional) {
    // `btrim` del lado de la base, `trim` acá: «  support  » es alguien
    // escribiendo, no otro valor.
    if ((puestas[c.dimension] ?? '').trim() === c.valor) dims.add(c.exige);
  }
  // Ordenadas, para que los campos no bailen de sitio cuando aparece una: el
  // `department` que se acaba de escribir hace nacer el de `modalidad`, y que
  // además se muevan los otros se lee como que algo se rompió.
  return [...dims].toSorted();
}

/**
 * Lo que se manda: sólo las dimensiones que la línea pide AHORA MISMO.
 *
 * ▶▶ LO ENSEÑÓ LA CAMINATA, y no falla — se guarda.
 *
 * Con `6110` y el área en `support` aparece el campo de `modalidad`. Si después
 * se cambia el área a `sales`, el campo **desaparece de la pantalla y el valor
 * se queda en el estado**. Sin esto se mandaba una dimensión que ya no se ve,
 * que no se puede quitar y que nadie volvería a mirar: un salario de ventas
 * guardado con modalidad `saas`.
 *
 * La base lo acepta —`saas` está en el vocabulario— y ése es justamente el
 * problema: no hay ningún error que lo denuncie. Aparecería en cualquier
 * informe que agrupe por modalidad, como gasto de una modalidad que nadie
 * escribió.
 */
export function soloLoQueSePide(
  puestas: Readonly<Record<string, string>>,
  exigidas: readonly string[],
): Record<string, string> {
  const pedidas = new Set(exigidas);
  return Object.fromEntries(
    Object.entries(puestas).filter(([k]) => pedidas.has(k)));
}
